import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Edit, 
  Building2,
  User,
  MapPin,
  Ship,
  Mail,
  Phone,
  FileText,
  Calendar,
  Tag,
  Globe,
  Package,
  Truck
} from 'lucide-react';

const DetailClient = () => {
  const [client, setClient] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  // Simulation d'un client récupéré par ID (dans une vraie app, ceci viendrait de l'API)
  useEffect(() => {
    // Simuler un client récupéré par ID depuis l'URL
    const mockClient = {
      id: 1,
      customer_code: 'CMA001',
      social_reason: 'CMA CGM',
      siren: '123456789',
      naf: '5010',
      tva_number: 'FR12345678901',
      logo_url: '/logos/cmacgm.png',
      adresse: '4 Quai d\'Arenc',
      postal_code: '13002',
      city: 'Marseille',
      country: 'France',
      code_comptable: 'CMA001',
      notes: 'Client principal - Transport maritime. Spécialisé dans le transport de conteneurs et la logistique portuaire. Partenaire de longue date d\'ATL.',
      statut: 'Actif',
      date_creation: '2024-01-01',
      date_modification: '2024-01-15',
      nombreLivraisons: 45,
      recipients: [
        {
          id: 1,
          label: 'CMA CGM Marseille',
          imo_number: 'IMO1234567',
          type: 'Company',
          adresse: '4 Quai d\'Arenc',
          postal_code: '13002',
          city: 'Marseille',
          country: 'France',
          notes: 'Siège principal et terminal principal'
        },
        {
          id: 2,
          label: 'CMA CGM Le Havre',
          imo_number: '',
          type: 'Company',
          adresse: 'Quai de la Manche',
          postal_code: '76600',
          city: 'Le Havre',
          country: 'France',
          notes: 'Terminal secondaire'
        }
      ],
      contacts: [
        {
          id: 1,
          name: 'Jean Dupont',
          email: 'contact@cmacgm.com',
          telephone: '+33 1 23 45 67 89',
          notes: 'Contact principal - Responsable logistique'
        },
        {
          id: 2,
          name: 'Marie Martin',
          email: 'marie.martin@cmacgm.com',
          telephone: '+33 1 23 45 67 90',
          notes: 'Contact secondaire - Assistante logistique'
        }
      ],
      livraisons: [
        {
          id: 'LIV-2024-001',
          date: '2024-01-15',
          statut: 'Terminée',
          poids: '2.5 tonnes',
          conditionnement: 'Conteneur 40\'',
          destination: 'Marseille'
        },
        {
          id: 'LIV-2024-002',
          date: '2024-01-14',
          statut: 'En cours',
          poids: '1.8 tonnes',
          conditionnement: 'Palette',
          destination: 'Le Havre'
        }
      ]
    };
    
    setClient(mockClient);
  }, []);

  const tabs = [
    { id: 'general', label: 'Informations générales', icon: Building2 },
    { id: 'address', label: 'Adresse', icon: MapPin },
    { id: 'recipients', label: 'Destinataires', icon: Ship },
    { id: 'contacts', label: 'Contacts', icon: User },
    { id: 'livraisons', label: 'Livraisons', icon: Truck }
  ];

  const renderTabContent = () => {
    if (!client) return null;

    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            {/* En-tête avec logo et statut */}
            <div className="flex items-start space-x-6">
              {client.logo_url ? (
                <img src={client.logo_url} alt={client.social_reason} className="h-24 w-24 rounded-lg object-cover" />
              ) : (
                <div className="h-24 w-24 rounded-lg bg-atl-blue-100 flex items-center justify-center">
                  <Building2 className="h-12 w-12 text-atl-blue-600" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h2 className="text-2xl font-bold text-port-gray-900">{client.social_reason}</h2>
                  <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                    client.statut === 'Actif' ? 'bg-atl-green-100 text-atl-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {client.statut}
                  </span>
                </div>
                <p className="text-port-gray-600 mb-4">{client.notes}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-port-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-atl-blue-600">{client.nombreLivraisons}</div>
                    <div className="text-sm text-port-gray-600">Livraisons</div>
                  </div>
                  <div className="text-center p-3 bg-port-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-atl-green-600">{client.recipients?.length || 0}</div>
                    <div className="text-sm text-port-gray-600">Destinataires</div>
                  </div>
                  <div className="text-center p-3 bg-port-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-atl-blue-600">{client.contacts?.length || 0}</div>
                    <div className="text-sm text-port-gray-600">Contacts</div>
                  </div>
                  <div className="text-center p-3 bg-port-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-port-gray-600">{client.livraisons?.length || 0}</div>
                    <div className="text-sm text-port-gray-600">Ce mois</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Informations détaillées */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card p-6">
                <h3 className="text-lg font-semibold text-port-gray-900 mb-4 flex items-center">
                  <Tag className="h-5 w-5 mr-2 text-atl-blue-600" />
                  Informations d'identification
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-port-gray-600">Code client:</span>
                    <span className="font-medium text-port-gray-900">{client.customer_code}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-port-gray-600">Code comptable:</span>
                    <span className="font-medium text-port-gray-900">{client.code_comptable}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-port-gray-600">SIREN:</span>
                    <span className="font-medium text-port-gray-900">{client.siren}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-port-gray-600">Code NAF:</span>
                    <span className="font-medium text-port-gray-900">{client.naf}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-port-gray-600">Numéro TVA:</span>
                    <span className="font-medium text-port-gray-900">{client.tva_number}</span>
                  </div>
                </div>
              </div>

              <div className="card p-6">
                <h3 className="text-lg font-semibold text-port-gray-900 mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-atl-blue-600" />
                  Informations temporelles
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-port-gray-600">Date de création:</span>
                    <span className="font-medium text-port-gray-900">
                      {new Date(client.date_creation).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-port-gray-600">Dernière modification:</span>
                    <span className="font-medium text-port-gray-900">
                      {new Date(client.date_modification).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-port-gray-600">Statut:</span>
                    <span className={`font-medium ${
                      client.statut === 'Actif' ? 'text-atl-green-600' : 'text-red-600'
                    }`}>
                      {client.statut}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'address':
        return (
          <div className="space-y-6">
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-port-gray-900 mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2 text-atl-blue-600" />
                Adresse principale
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-port-gray-700 mb-1">Adresse complète</label>
                  <p className="text-port-gray-900">{client.adresse}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-port-gray-700 mb-1">Code postal</label>
                    <p className="text-port-gray-900">{client.postal_code}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-port-gray-700 mb-1">Ville</label>
                    <p className="text-port-gray-900">{client.city}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-port-gray-700 mb-1">Pays</label>
                    <p className="text-port-gray-900">{client.country}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'recipients':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-port-gray-900">Destinataires ({client.recipients?.length || 0})</h3>
            </div>
            
            {client.recipients && client.recipients.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {client.recipients.map((recipient) => (
                  <div key={recipient.id} className="card p-6">
                    <div className="flex items-start space-x-3 mb-4">
                      {recipient.type === 'Ship' ? (
                        <Ship className="h-8 w-8 text-atl-blue-600" />
                      ) : (
                        <Building2 className="h-8 w-8 text-atl-green-600" />
                      )}
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-port-gray-900">{recipient.label}</h4>
                        <p className="text-sm text-port-gray-600 capitalize">{recipient.type}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {recipient.imo_number && (
                        <div className="flex justify-between">
                          <span className="text-port-gray-600">Numéro IMO:</span>
                          <span className="font-medium text-port-gray-900">{recipient.imo_number}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-port-gray-600">Adresse:</span>
                        <span className="font-medium text-port-gray-900">{recipient.adresse}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-port-gray-600">Localisation:</span>
                        <span className="font-medium text-port-gray-900">
                          {recipient.postal_code} {recipient.city}, {recipient.country}
                        </span>
                      </div>
                      {recipient.notes && (
                        <div>
                          <label className="block text-sm font-medium text-port-gray-700 mb-1">Notes</label>
                          <p className="text-port-gray-900 text-sm">{recipient.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-port-gray-500">
                <Ship className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Aucun destinataire défini</p>
              </div>
            )}
          </div>
        );

      case 'contacts':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-port-gray-900">Contacts ({client.contacts?.length || 0})</h3>
            </div>
            
            {client.contacts && client.contacts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {client.contacts.map((contact) => (
                  <div key={contact.id} className="card p-6">
                    <div className="flex items-start space-x-3 mb-4">
                      <User className="h-8 w-8 text-atl-blue-600" />
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-port-gray-900">{contact.name}</h4>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-port-gray-400" />
                        <span className="text-port-gray-900">{contact.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-port-gray-400" />
                        <span className="text-port-gray-900">{contact.telephone}</span>
                      </div>
                      {contact.notes && (
                        <div>
                          <label className="block text-sm font-medium text-port-gray-700 mb-1">Notes</label>
                          <p className="text-port-gray-900 text-sm">{contact.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-port-gray-500">
                <User className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Aucun contact défini</p>
              </div>
            )}
          </div>
        );

      case 'livraisons':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-port-gray-900">Livraisons récentes ({client.livraisons?.length || 0})</h3>
            </div>
            
            {client.livraisons && client.livraisons.length > 0 ? (
              <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-port-gray-200">
                    <thead className="bg-port-gray-50">
                      <tr>
                        <th className="table-header px-6 py-3 text-left">ID Livraison</th>
                        <th className="table-header px-6 py-3 text-left">Date</th>
                        <th className="table-header px-6 py-3 text-left">Statut</th>
                        <th className="table-header px-6 py-3 text-left">Poids</th>
                        <th className="table-header px-6 py-3 text-left">Conditionnement</th>
                        <th className="table-header px-6 py-3 text-left">Destination</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-port-gray-200">
                      {client.livraisons.map((livraison) => (
                        <tr key={livraison.id} className="table-row">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Package className="h-5 w-5 text-atl-blue-600 mr-2" />
                              <span className="font-medium text-port-gray-900">{livraison.id}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-port-gray-900">
                              {new Date(livraison.date).toLocaleDateString('fr-FR')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              livraison.statut === 'Terminée' ? 'bg-atl-green-100 text-atl-green-800' :
                              livraison.statut === 'En cours' ? 'bg-atl-blue-100 text-atl-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {livraison.statut}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-port-gray-900">{livraison.poids}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-port-gray-900">{livraison.conditionnement}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-port-gray-900">{livraison.destination}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-port-gray-500">
                <Truck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Aucune livraison trouvée</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (!client) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-atl-blue-600 mx-auto mb-4"></div>
          <p className="text-port-gray-600">Chargement du client...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => window.history.back()}
            className="btn-secondary flex items-center space-x-2"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Retour</span>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-port-gray-900">Détails du client</h1>
            <p className="text-port-gray-600">Informations complètes sur {client.social_reason}</p>
          </div>
        </div>
        <button 
          onClick={() => window.location.href = `/modifier-client/${client.id}`}
          className="btn-primary flex items-center space-x-2"
        >
          <Edit className="h-5 w-5" />
          <span>Modifier</span>
        </button>
      </div>

      {/* Onglets */}
      <div className="border-b border-port-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-atl-blue-500 text-atl-blue-600'
                    : 'border-transparent text-port-gray-500 hover:text-port-gray-700 hover:border-port-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Contenu des onglets */}
      <div className="card p-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default DetailClient;
